import * as React from "react";
import { createPortal } from "react-dom";
import { AppStateContext } from "../../state/AppProvider";
import { GroupedChatHistory } from "./types";
import { DeleteDialog } from "./DeleteDialog"
import { Conversation } from "../../api/models";
import { useEffect, useRef, useState } from "react";
import { throttle } from "lodash";
import { renameConversation, deleteConversation, pinConversation, unpinConversation } from "../../api";
import { Transition } from "@headlessui/react";
import { useClearChat } from "../ClearChatButton/ClearChatContext";

interface ChatHistoryListItemGroupsProps {
    groupedChatHistory: GroupedChatHistory[];
}

const formatMonth = (month: string) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const [monthName, yearString] = month.split(" ");
    const year = parseInt(yearString);
    return year === currentYear ? monthName : month;
};

export const ChatHistoryListItemGroups: React.FC<ChatHistoryListItemGroupsProps> = ({ groupedChatHistory }) => {
    const appStateContext = React.useContext(AppStateContext);
    const [, setSelectedItem] = React.useState<Conversation | null>(null);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [menuFor, setMenuFor] = useState<Conversation | null>(null);
    const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingValue, setEditingValue] = useState<string>("");
    const [pendingId, setPendingId] = useState<string | null>(null);
    const [confirmDeleteFor, setConfirmDeleteFor] = useState<Conversation | null>(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const editInputRef = useRef<HTMLInputElement | null>(null);
    const clearChat = useClearChat();

    useEffect(() => {
        const handleResize = throttle(() => setWindowWidth(window.innerWidth), 200);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (editingId && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [editingId]);

    const handleSelectItem = (item: Conversation) => {
        if (appStateContext?.state.answerLoading) return;
        setSelectedItem(item);
        appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: item });
        if (windowWidth < 639) appStateContext?.dispatch({ type: "TOGGLE_CHAT_HISTORY" });
    };

    const toggleMenu = (e: React.MouseEvent, entry: Conversation) => {
        e.stopPropagation();
        e.preventDefault();

        if (menuFor?.id === entry.id) {
            setMenuFor(null);
            setMenuPos(null);
            return;
        }

        const btnEl = e.currentTarget as HTMLElement;
        const hostRect = btnEl.parentElement!.getBoundingClientRect();
        const rowEl = btnEl.previousElementSibling as HTMLElement | null;
        const rowRect = rowEl ? rowEl.getBoundingClientRect() : hostRect;

        setMenuPos({ top: rowRect.bottom, right: window.innerWidth - hostRect.right });
        setMenuFor(entry);
    };

    const openConfirmDelete = (entry: Conversation) => {
        setMenuFor(null);
        setMenuPos(null);
        setConfirmDeleteFor(entry);
        setIsDeleteOpen(true);
    };

    const startInlineRename = (entry: Conversation) => {
        setMenuFor(null);
        setMenuPos(null);
        setEditingId(entry.id);
        setEditingValue(entry.title);
    };

    const pinItem = async (entry: Conversation) => {
        appStateContext?.dispatch({ type: "PIN_CHAT_HISTORY", payload: entry.id });
        setMenuFor(null);
        setMenuPos(null);

        try {
            await pinConversation(entry.id);
        } catch (err) {
            appStateContext?.dispatch({ type: "UNPIN_CHAT_HISTORY", payload: entry.id });
            console.error(err);
        }
    };

    const unpinItem = async (entry: Conversation) => {
        appStateContext?.dispatch({ type: "UNPIN_CHAT_HISTORY", payload: entry.id });
        setMenuFor(null);
        setMenuPos(null);

        try {
            await unpinConversation(entry.id);
        } catch (err) {
            appStateContext?.dispatch({ type: "PIN_CHAT_HISTORY", payload: entry.id });
            console.error(err);
        }
    };


    const commitInlineRename = async (entry: Conversation) => {
        const trimmed = editingValue.trim();
        setEditingId(null);
        if (!trimmed || trimmed === entry.title) return;

        try {
            setPendingId(entry.id);
            const updated = await renameConversation(entry.id, trimmed);
            appStateContext?.dispatch({ type: "UPDATE_CHAT_TITLE", payload: { ...entry, title: updated.title } });
        } catch (err) {
            console.error("Failed to rename conversation", err);
        } finally {
            setPendingId(null);
        }
    };

    const cancelInlineRename = () => {
        setEditingId(null);
    };

    const performDelete = async () => {
        if (!confirmDeleteFor) return;
        try {
            setPendingId(confirmDeleteFor.id);
            const result = await deleteConversation(confirmDeleteFor.id);
            if (result.deleted_conversation_id) {
                appStateContext?.dispatch({ type: "DELETE_CHAT_ENTRY", payload: confirmDeleteFor.id });
            }
            if (appStateContext?.state.currentChat?.id === confirmDeleteFor.id) {
                clearChat();
                appStateContext?.dispatch({
                    type: "UPDATE_ASSISTANT",
                    payload: appStateContext?.state.assistants?.filter((assistant) => assistant.assistant === "gpt4")[0] ?? null
                });

            }
        } catch (err) {
            console.error("Failed to delete conversation", err);
        } finally {
            setPendingId(null);
            setIsDeleteOpen(false);
        }
    };

    return (
        <div>
            {groupedChatHistory.map(
                group =>
                    group.entries.length > 0 && (
                        <div className="relative mt-5 first:mt-0 last:mb-2" key={group.month}>
                            <div className="sticky top-0 z-30 bg-jtc-grey">
                                <span className="flex h-9 items-center">
                                    <h3 className="pb-2 pt-3 px-2 text-xs font-semibold text-jtc-purple overflow-hidden text-ellipsis break-all whitespace-nowrap">
                                        {formatMonth(group.month)}
                                    </h3>
                                </span>
                            </div>

                            <ol>
                                {group.entries.map(entry => {
                                    const isOpen = menuFor?.id === entry.id;
                                    const isActive = entry.id === appStateContext?.state.currentChat?.id;
                                    const isEditing = editingId === entry.id;
                                    const isPending = pendingId === entry.id;

                                    return (
                                        <li className="relative z-20" key={entry.id}>
                                            <div className="relative rounded-lg group">
                                                {isEditing ? (
                                                    <div className="flex items-center gap-2 p-2 pr-10 rounded-lg bg-jtc-white">
                                                        <input
                                                            ref={editInputRef}
                                                            value={editingValue}
                                                            onChange={e => setEditingValue(e.target.value)}
                                                            onKeyDown={e => {
                                                                if (e.key === "Enter") {
                                                                    e.preventDefault();
                                                                    commitInlineRename(entry);
                                                                } else if (e.key === "Escape") {
                                                                    e.preventDefault();
                                                                    cancelInlineRename();
                                                                }
                                                            }}
                                                            onBlur={cancelInlineRename}
                                                            className="w-full bg-transparent text-jtc-very-dark-grey outline-none border-none p-0 m-0 leading-normal"
                                                        />
                                                    </div>
                                                ) : (
                                                    <a
                                                        onClick={() => handleSelectItem(entry)}
                                                        className={
                                                            "flex items-center gap-2 p-2 pr-10 rounded-lg " +
                                                            ((isActive || isOpen) ? "bg-jtc-white" : "hover:bg-jtc-white") +
                                                            (appStateContext?.state.answerLoading || isPending ? " cursor-wait" : " cursor-pointer")
                                                        }
                                                    >
                                                        <div className="grow truncate">{entry.title}</div>
                                                    </a>
                                                )}

                                                <button
                                                    type="button"
                                                    aria-label="More options"
                                                    disabled={isPending}
                                                    className={
                                                        "absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 " +
                                                        "flex items-center justify-center h-6 w-6 " +
                                                        (isOpen ? "" : "opacity-0 group-hover:opacity-100") +
                                                        (isPending ? " cursor-wait" : "")
                                                    }
                                                    onClick={e => toggleMenu(e, entry)}
                                                >
                                                    <span className="material-symbols-outlined text-[20px] leading-none">more_horiz</span>
                                                </button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    )
            )}

            {menuFor && menuPos && createPortal(
                <>
                    <div
                        className="fixed inset-0 z-[9998]"
                        onMouseDown={() => { setMenuFor(null); setMenuPos(null); }}
                    />
                    <Transition
                        appear
                        show={!!menuFor}
                        enter="transition ease-out duration-75"
                        enterFrom="opacity-0 scale-90"
                        enterTo="opacity-100 scale-100"
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-90"
                        afterLeave={() => { setMenuPos(null); }}
                    >
                        <div
                            className="fixed w-36 rounded-md bg-jtc-white border border-jtc-grey z-[9999] overflow-hidden translate-y-1"
                            style={{ top: `${menuPos.top}px`, right: `${menuPos.right}px` }}
                            onMouseDown={e => e.stopPropagation()}
                        >
                            {menuFor?.pinned === true ? (
                                <button
                                    className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-jtc-very-dark-grey hover:bg-[rgba(75,75,75,0.05)]"
                                    onClick={() => { if (menuFor) unpinItem(menuFor); }}
                                >
                                    <span className="material-symbols-outlined text-[18px] leading-none" aria-hidden="true">push_pin</span>
                                    <span>Unpin</span>
                                </button>
                            ) : (
                                <button
                                    className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-jtc-very-dark-grey hover:bg-[rgba(75,75,75,0.05)]"
                                    onClick={() => { if (menuFor) pinItem(menuFor); }}
                                >
                                    <span className="material-symbols-outlined text-[18px] leading-none" aria-hidden="true">push_pin</span>
                                    <span>Pin</span>
                                </button>
                            )}
                            <button
                                className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-jtc-very-dark-grey hover:bg-[rgba(75,75,75,0.05)]"
                                onClick={() => { if (menuFor) startInlineRename(menuFor); }}
                            >
                                <span className="material-symbols-outlined text-[18px] leading-none" aria-hidden="true">edit</span>
                                <span>Rename</span>
                            </button>
                            <button
                                className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-jtc-pink hover:bg-[rgba(186,42,132,0.1)]"
                                onClick={() => { if (menuFor) openConfirmDelete(menuFor); }}
                            >
                                <span className="material-symbols-outlined text-[18px] leading-none" aria-hidden="true">delete</span>
                                <span>Delete</span>
                            </button>
                        </div>
                    </Transition>
                </>,
                document.body
            )}


            {createPortal(
                <DeleteDialog
                    open={isDeleteOpen}
                    conversation={confirmDeleteFor}
                    onClose={() => setIsDeleteOpen(false)}
                    onAfterLeave={() => setConfirmDeleteFor(null)}
                    onConfirm={performDelete}
                    isDeleting={pendingId === confirmDeleteFor?.id}
                />,
                document.body
            )}
        </div>
    );
};
